// src/services/modelLoaderService.js
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { clone as cloneSkeleton } from 'three/examples/jsm/utils/SkeletonUtils.js';
import * as THREE from 'three';
import { config } from '../config.js';
import logger from '../utils/debugLogger.js';
// --- UPDATE START: Import localforage ---
import localForage from 'localforage';
// --- UPDATE END ---

const DEFAULT_TIMEOUT_MS = 30000;
const FALLBACK_SIZE = 2;      // World meters, scaled by caller
const MAX_CACHE_ENTRIES = 60; // Simple LRU cap to avoid memory leaks

// --- UPDATE START: Configure localForage ---
localForage.config({
    name: 'FlowSenseModelCache',
    storeName: 'models',
    description: 'Persistent cache for 3D GLB models'
});
// --- UPDATE END ---

class ModelLoaderService {
  constructor() {
    this.loader = new GLTFLoader();
    this.cache = new Map();           // Stores original loaded scenes (In-Memory Cache)
    this.loadingPromises = new Map(); // Stores promises for ongoing loads
    this.baseUrl = config.apiUrl.replace('/api/v1', '');
  }

  /**
   * Creates a standardized fallback mesh (a grey cube).
   * @returns {THREE.Mesh} A new fallback mesh.
   */
  createFallbackObject() {
    const geometry = new THREE.BoxGeometry(FALLBACK_SIZE, FALLBACK_SIZE, FALLBACK_SIZE);
    const material = new THREE.MeshStandardMaterial({
      color: 0x808080,
      name: 'FallbackMaterial'
    });
    const fallbackMesh = new THREE.Mesh(geometry, material);
    fallbackMesh.name = 'FallbackObject';
    fallbackMesh.userData.assetType = 'fallback';
    return fallbackMesh;
  }

  /**
   * Enforces a simple LRU cache limit by evicting the oldest entry if the cache exceeds its maximum size.
   */
  enforceCacheLimit() {
    if (this.cache.size > MAX_CACHE_ENTRIES) {
      const oldestKey = this.cache.keys().next().value;
      this.cache.delete(oldestKey);
      logger.warn(`[ModelLoaderService] Evicted from cache due to size limit: ${oldestKey}`);
    }
  }

  /**
   * Loads a 3D model with caching, timeout, and robust fallback handling.
   * This function will ALWAYS resolve to a valid 3D object.
   * @param {string | null | undefined} url - The relative URL of the model.
   * @returns {Promise<THREE.Object3D>} A promise that resolves to a clone of the 3D object.
   */
  async loadModel(url) {
    if (!url || typeof url !== 'string') {
      logger.warn('[ModelLoaderService] Invalid or missing URL provided. Returning fallback.');
      return this.createFallbackObject();
    }

    let absoluteUrl;
    try {
      absoluteUrl = new URL(url, this.baseUrl).href;
    } catch (e) {
      logger.error(`[ModelLoaderService] Malformed URL encountered: ${url}`, e);
      return this.createFallbackObject();
    }

    // --- Cache Handling ---
    // 1. Check in-memory cache (fastest)
    if (this.cache.has(absoluteUrl)) {
      const cachedScene = this.cache.get(absoluteUrl);
      this.cache.delete(absoluteUrl);
      this.cache.set(absoluteUrl, cachedScene);
      return cloneSkeleton(cachedScene);
    }
    
    // --- UPDATE START: Add IndexedDB (persistent) cache check ---
    // 2. Check persistent cache (survives refresh)
    try {
        const cachedModelData = await localForage.getItem(absoluteUrl);
        if (cachedModelData) {
            logger.log(`[ModelLoaderService] Loaded from IndexedDB: ${absoluteUrl}`);
            // We store the raw GLTF data, so we need to parse it
            const gltf = await this.loader.parseAsync(cachedModelData, '');
            const scene = gltf.scene;
            scene.userData.sourceUrl = absoluteUrl;
            this.cache.set(absoluteUrl, scene); // Add to in-memory cache for this session
            this.enforceCacheLimit();
            return cloneSkeleton(scene);
        }
    } catch (e) {
        logger.warn(`[ModelLoaderService] Failed to read from IndexedDB, will fetch from network.`, e);
    }
    // --- UPDATE END ---


    // --- Deduplication of Concurrent Requests ---
    if (this.loadingPromises.has(absoluteUrl)) {
      const originalScene = await this.loadingPromises.get(absoluteUrl);
      return cloneSkeleton(originalScene);
    }

    // --- Fetch and Load New Model ---
    // --- UPDATE START: We must now fetch the raw ArrayBuffer to store it ---
    const promise = new Promise((resolve) => {
        const loadingTimeout = setTimeout(() => {
            logger.error(`[ModelLoaderService] Loading timed out for: ${absoluteUrl}`);
            this.loadingPromises.delete(absoluteUrl);
            resolve(this.createFallbackObject()); // Resolve with fallback on timeout
        }, DEFAULT_TIMEOUT_MS);

        // Fetch the raw model data (ArrayBuffer)
        fetch(absoluteUrl)
            .then(response => {
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                return response.arrayBuffer();
            })
            .then(arrayBuffer => {
                // Now, parse the ArrayBuffer
                this.loader.parse(
                    arrayBuffer,
                    '', // The base path
                    (gltf) => { // onSuccess
                        clearTimeout(loadingTimeout);
                        const scene = gltf.scene || this.createFallbackObject();
                        
                        if (scene.children.length === 0) {
                            logger.warn(`[ModelLoaderService] GLTF loaded but scene is empty for ${absoluteUrl}. Using fallback.`);
                            resolve(this.createFallbackObject());
                        } else {
                            scene.userData.sourceUrl = absoluteUrl;
                            this.cache.set(absoluteUrl, scene); // Add to in-memory cache
                            this.enforceCacheLimit();
                            logger.log(`[ModelLoaderService] Successfully loaded and cached model: ${absoluteUrl}`);
                            
                            // Save the raw buffer to IndexedDB
                            localForage.setItem(absoluteUrl, arrayBuffer).catch(e => {
                                logger.warn(`[ModelLoaderService] Failed to save to IndexedDB.`, e);
                            });
                            
                            resolve(scene);
                        }
                    },
                    (error) => { // onError
                        clearTimeout(loadingTimeout);
                        logger.error(`[ModelLoaderService] Failed to PARSE model from ${absoluteUrl}. Returning fallback.`, error);
                        resolve(this.createFallbackObject());
                    }
                );
            })
            .catch(error => {
                clearTimeout(loadingTimeout);
                logger.error(`[ModelLoaderService] Failed to FETCH model from ${absoluteUrl}. Returning fallback.`, error);
                resolve(this.createFallbackObject());
            });

    }).finally(() => {
        // Once this request is settled, remove its promise
        this.loadingPromises.delete(absoluteUrl);
    });
    // --- UPDATE END ---

    this.loadingPromises.set(absoluteUrl, promise);
    
    // Return a clone of the resolved scene
    const resolvedScene = await promise;
    return cloneSkeleton(resolvedScene);
  }
}

// Export a singleton instance of the service
const modelLoaderService = new ModelLoaderService();
export default modelLoaderService;